// Custom hook for charging type management
import { useState, useEffect } from 'react';
import moment from 'moment';

export const useChargingType = (form, spaceDetails) => {
  const [chargingType, setChargingType] = useState(null);
  const [availableBlocks, setAvailableBlocks] = useState([]);
  const [availableChargingTypes, setAvailableChargingTypes] = useState([]);
  const [selectedChargingOption, setSelectedChargingOption] = useState(null);

  useEffect(() => {
    if (spaceDetails) {
      let availableCharging = [];
      const chargings = spaceDetails.events?.[0]?.chargings || spaceDetails.chargings || [];
      
      if (chargings.length > 0) {
        availableCharging = chargings.map(charging => {
          const chargingTypeName = charging.chargingType?.name || 'Hourly';
          const chargingTypeNameLower = chargingTypeName.toLowerCase();
          
          let price = 0;
          if (charging.pricingOptions && charging.pricingOptions.length > 0) {
            price = charging.pricingOptions[0]?.price?.amount || 0;
          }
          
          let blocks = charging.blocks || [];
          if (chargingTypeNameLower === 'block' && charging.pricingOptions && charging.pricingOptions.length > 0) {
            blocks = charging.pricingOptions.map((option, idx) => ({
              id: idx,
              name: option.name || `Block ${idx + 1}`,
              startTime: option.startTime,
              endTime: option.endTime,
              price: option.price?.amount || option.pricePerHour || 0
            }));
          }
          
          return {
            id: String(charging.id || charging.chargingType?.id),
            type: chargingTypeNameLower,
            name: chargingTypeName,
            hourlyRate: charging.hourlyRate || (chargingTypeNameLower === 'hourly' ? price : null),
            dailyRate: charging.dailyRate || (chargingTypeNameLower === 'daily' ? price : null),
            weeklyRate: charging.weeklyRate || (chargingTypeNameLower === 'weekly' ? price : null),
            monthlyRate: charging.monthlyRate || (chargingTypeNameLower === 'monthly' ? price : null),
            blockRate: charging.blockRate || (chargingTypeNameLower === 'block' ? price : null),
            unit: charging.unit,
            minimumBookingPeriod: charging.minimumBookingPeriod || charging.minimumBookingHours || 1,
            minimumBookingHours: charging.minimumBookingPeriod || charging.minimumBookingHours || 1,
            minimumBookingPeriodAvailability: charging.minimumBookingPeriodAvailability,
            acceptedHalfHourBooking: charging.acceptedHalfHourBooking,
            blocks: blocks,
            pricingOptions: charging.pricingOptions || [],
            discounts: charging.discounts || [],
            packages: charging.packages || [],
            charging: charging
          };
        });
          
        setAvailableChargingTypes(availableCharging);
        
        if (availableCharging.length > 0) {
          const defaultCharging = availableCharging[0];
          const defaultPrice = defaultCharging.hourlyRate || defaultCharging.dailyRate || 
                        defaultCharging.weeklyRate || defaultCharging.monthlyRate || 
                        defaultCharging.blockRate || 0;
          
          setChargingType(defaultCharging.type);
          setSelectedChargingOption(defaultCharging);
          
          if (defaultCharging.type === 'block') {
            setAvailableBlocks(defaultCharging.blocks);
          }
          
          form.setFieldsValue({
            bookingValue: defaultPrice,
            selectedChargingType: String(defaultCharging.id)
          });
        }
      }
    } else {
      // Reset charging type states when spaceDetails is cleared
      setChargingType(null);
      setSelectedChargingOption(null);
      setAvailableChargingTypes([]);
      setAvailableBlocks([]);
    }
  }, [spaceDetails, form]);

  const handleChargingTypeChange = (chargingId) => {
    const selectedCharging = availableChargingTypes.find(c => String(c.id) === String(chargingId));
    if (selectedCharging) {
      setSelectedChargingOption(selectedCharging);
      setChargingType(selectedCharging.type);

      const newPrice = selectedCharging.hourlyRate || selectedCharging.dailyRate ||
        selectedCharging.weeklyRate || selectedCharging.monthlyRate ||
        selectedCharging.blockRate || 0;

      form.setFieldsValue({
        bookingValue: newPrice,
        selectedChargingType: String(chargingId)
      });

      if (selectedCharging.type === 'block') {
        setAvailableBlocks(selectedCharging.blocks || []);
      } else {
        setAvailableBlocks([]);
      }

      // Clear previous booking period fields
      form.setFieldsValue({
        bookingStartTime: null,
        bookingEndTime: null,
        hourlyDuration: null,
        selectedBlock: null,
        blockBookingDate: null,
        dailyStartDate: null,
        dailyEndDate: null,
        numberOfDays: null,
        weeklyStartDate: null,
        numberOfWeeks: null,
        monthlyStartDate: null,
        numberOfMonths: null
      });

      // Set sensible defaults for the newly selected charging type
      const today = moment();
      const parseTime = (t) => t ? moment(t, ['h:mm a','HH:mm','h:mm A','H:mm']) : null;
      const pricingStart = selectedCharging.pricingOptions?.[0]?.startTime;
      const startTimeMoment = parseTime(pricingStart) || moment('09:00','HH:mm');

      if (selectedCharging.type === 'hourly') {
        const start = moment(today).hour(startTimeMoment.hour()).minute(startTimeMoment.minute());
        const end = moment(start).add(1, 'hour');
        form.setFieldsValue({
          hourlyBookingDate: today,
          bookingStartTime: start,
          bookingEndTime: end,
          hourlyDuration: 1
        });
      }

      if (selectedCharging.type === 'daily') {
        form.setFieldsValue({
          dailyStartDate: today,
          dailyEndDate: today,
          numberOfDays: 1
        });
      }

      if (selectedCharging.type === 'weekly') {
        form.setFieldsValue({
          weeklyStartDate: today,
          numberOfWeeks: 1
        });
      }

      if (selectedCharging.type === 'monthly') {
        form.setFieldsValue({
          monthlyStartDate: today,
          numberOfMonths: 1
        });
      }

      if (selectedCharging.type === 'block') {
        const firstBlock = (selectedCharging.blocks && selectedCharging.blocks[0]) || null;
        if (firstBlock) {
          const blockId = firstBlock.id != null ? firstBlock.id : 0;
          form.setFieldsValue({
            selectedBlock: blockId,
            blockBookingDate: today
          });
          // If block has a specific price, reflect it
          if (firstBlock.price) {
            form.setFieldsValue({ bookingValue: firstBlock.price });
          }
        } else {
          form.setFieldsValue({ blockBookingDate: today });
        }
      }
    }
  };

  return {
    chargingType,
    availableBlocks,
    availableChargingTypes,
    selectedChargingOption,
    handleChargingTypeChange
  };
};
